import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Shield, Clock, XCircle, Sun, Moon, LogOut } from 'lucide-react';

export default function PendingApproval() {
    const { user, logout } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const navigate = useNavigate();

    const isRejected = user?.role === 'officer_rejected';

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    return (
        <div className="min-h-screen bg-background flex flex-col">
            <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="fixed top-4 right-4 z-50"
            >
                {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>

            <div className="flex-1 flex items-center justify-center p-4">
                <Card className="w-full max-w-md shadow-xl">
                    <CardHeader className="text-center pb-2">
                        <div className="flex justify-center mb-4">
                            <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                                isRejected ? 'bg-destructive/10' : 'bg-warning/10'
                            }`}>
                                {isRejected
                                    ? <XCircle className="w-9 h-9 text-destructive" />
                                    : <Clock className="w-9 h-9 text-warning" />
                                }
                            </div>
                        </div>
                        <CardTitle className="text-2xl">
                            {isRejected ? 'Application Rejected' : 'Pending Approval'}
                        </CardTitle>
                        <CardDescription>
                            {isRejected
                                ? 'Your officer registration was not approved.'
                                : 'Your officer registration is under review.'}
                        </CardDescription>
                    </CardHeader>

                    <CardContent className="space-y-4 pt-2">
                        {isRejected ? (
                            <div className="p-4 rounded-lg bg-destructive/5 border border-destructive/20 space-y-2">
                                <p className="text-sm font-medium text-destructive">Why was my application rejected?</p>
                                {user?.rejection_reason ? (
                                    <p className="text-sm text-muted-foreground">"{user.rejection_reason}"</p>
                                ) : (
                                    <p className="text-sm text-muted-foreground">
                                        No reason was provided. Please contact the admin for more details.
                                    </p>
                                )}
                            </div>
                        ) : (
                            <div className="space-y-3">
                                <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50">
                                    <Shield className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                                    <div className="text-sm">
                                        <p className="font-medium">Application Submitted</p>
                                        <p className="text-muted-foreground mt-1">
                                            Hello <span className="font-medium">{user?.name}</span>, your officer
                                            registration has been received and is awaiting admin review.
                                        </p>
                                    </div>
                                </div>
                                <div className="text-sm text-muted-foreground space-y-1 px-1">
                                    <p>Your submitted details:</p>
                                    <ul className="list-disc list-inside space-y-1 pl-2">
                                        <li>Employment ID: <span className="font-medium text-foreground">{user?.employment_id ?? '—'}</span></li>
                                        <li>Division Area: <span className="font-medium text-foreground">{user?.division_area ?? '—'}</span></li>
                                    </ul>
                                </div>
                                <p className="text-xs text-muted-foreground px-1">
                                    You will be able to log in and access the officer dashboard once an admin approves your application.
                                </p>
                            </div>
                        )}

                        <Button
                            variant="outline"
                            className="w-full"
                            onClick={handleLogout}
                        >
                            <LogOut className="w-4 h-4 mr-2" />
                            {isRejected ? 'Back to Home' : 'Sign Out'}
                        </Button>
                    </CardContent>
                </Card>
            </div>

            <footer className="py-4 text-center text-xs text-muted-foreground border-t">
                <div className="flex items-center justify-center gap-2">
                    <img src="/logo.png" alt="" className="h-5 w-auto rounded" />
                    <span>TrafficWatch — Officer Portal</span>
                </div>
            </footer>
        </div>
    );
}
